import React, { useState, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import axiosClient from '../../services/axiosClient';
import Swal from 'sweetalert2';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const Permissions = () => {
	const [permissions, setPermissions] = useState([]);
	const [filteredPermissions, setFilteredPermissions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [view, setView] = useState('list'); // 'list', 'add', 'edit'
	const [currentPermission, setCurrentPermission] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedItems, setSelectedItems] = useState([]);
	const [formData, setFormData] = useState({
		name: '',
		display_name: '',
		description: '',
		guard_name: 'web'
	});

	useEffect(() => {
		fetchPermissions();
	}, []);

	useEffect(() => {
		let filtered = [...permissions];
		if (searchTerm) {
			filtered = filtered.filter(p =>
				p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				p.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
			);
		}
		setFilteredPermissions(filtered);
	}, [searchTerm, permissions]);

	const fetchPermissions = async () => {
		try {
			setLoading(true);
			const response = await axiosClient.get('/admin/permissions', {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const apiData = response?.data;
			let list = apiData?.result || apiData?.data || [];
			
			if (!Array.isArray(list)) {
				list = [];
			}
			
			setPermissions(list);
			setFilteredPermissions(list);
		} catch (err) {
			console.error('Error fetching permissions:', err);
			Swal.fire({
				title: 'Erreur !',
				text: 'Impossible de charger les permissions',
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setLoading(false);
		}
	};

	const handleAddPermission = async () => {
		try {
			const response = await axiosClient.post('/admin/permissions', formData, {
				headers: { 
					'Accept': 'application/json',
					'Content-Type': 'application/json',
					...getAuthHeader() 
				},
			});
			
			if (response.status === 200 || response.status === 201) {
				await fetchPermissions();
				setView('list');
				setFormData({ name: '', display_name: '', description: '', guard_name: 'web' });
				
				Swal.fire({
					title: 'Succès !',
					text: 'La permission a été ajoutée avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error adding permission:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de l\'ajout de la permission';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleEditPermission = async (permission) => {
		try {
			const response = await axiosClient.get(`/admin/permissions/${permission.id}`, {
				headers: { 'Accept': 'application/json', ...getAuthHeader() },
			});
			
			const permData = response?.data?.result || response?.data;
			
			setCurrentPermission(permData);
			setFormData({
				name: permData.name || '',
				display_name: permData.display_name || '',
				description: permData.description || '',
				guard_name: permData.guard_name || 'web'
			});
			setView('edit');
		} catch (err) {
			console.error('Error loading permission details:', err);
			Swal.fire({
				title: 'Erreur !',
				text: 'Impossible de charger les détails de la permission',
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleUpdatePermission = async () => {
		try {
			const response = await axiosClient.put(
				`/admin/permissions/${currentPermission.id}`, 
				formData,
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200) {
				await fetchPermissions();
				setView('list');
				setCurrentPermission(null);
				setFormData({ name: '', display_name: '', description: '', guard_name: 'web' });
				
				Swal.fire({
					title: 'Succès !',
					text: 'La permission a été mise à jour avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error updating permission:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la mise à jour de la permission';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleDeletePermission = async (permissionId, permissionName) => {
		const result = await Swal.fire({
			title: 'Supprimer la permission',
			text: `Voulez-vous vraiment supprimer la permission "${permissionName}" ? Cette action est irréversible.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			const response = await axiosClient.delete(
				`/admin/permissions/${permissionId}`,
				{
					headers: { 
						'Accept': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			if (response.status === 200 || response.status === 204) {
				await fetchPermissions();
				
				Swal.fire({
					title: 'Supprimée !',
					text: 'La permission a été supprimée avec succès.',
					icon: 'success',
					confirmButtonColor: '#28a745',
					timer: 2000
				});
			}
		} catch (err) {
			console.error('Error deleting permission:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de la suppression de la permission';
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const handleBulkDelete = async () => {
		if (selectedItems.length === 0) {
			Swal.fire({
				title: 'Attention !',
				text: 'Veuillez sélectionner au moins une permission à supprimer.',
				icon: 'warning',
				confirmButtonColor: '#28a745'
			});
			return;
		}

		const result = await Swal.fire({
			title: 'Supprimer les permissions',
			text: `Voulez-vous vraiment supprimer ${selectedItems.length} permission(s) ? Cette action est irréversible.`,
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, supprimer',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			await Promise.all(
				selectedItems.map(id =>
					axiosClient.delete(`/admin/permissions/${id}`, {
						headers: { 'Accept': 'application/json', ...getAuthHeader() }
					})
				)
			);
			
			await fetchPermissions();
			setSelectedItems([]);
			
			Swal.fire({
				title: 'Supprimées !',
				text: 'Les permissions ont été supprimées avec succès.',
				icon: 'success',
				confirmButtonColor: '#28a745',
				timer: 2000
			});
		} catch (err) {
			console.error('Error bulk deleting permissions:', err);
			Swal.fire({
				title: 'Erreur !',
				text: 'Erreur lors de la suppression des permissions',
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		}
	};

	const toggleSelectAll = () => {
		if (selectedItems.length === filteredPermissions.length) {
			setSelectedItems([]);
		} else {
			setSelectedItems(filteredPermissions.map(p => p.id));
		}
	};

	const toggleSelectItem = (id) => {
		if (selectedItems.includes(id)) {
			setSelectedItems(selectedItems.filter(item => item !== id));
		} else {
			setSelectedItems([...selectedItems, id]);
		}
	};

	// LIST VIEW
	if (view === 'list') {
		return (
			<>
				<style>
					{`
						.permissions-table {
							background: white;
							border-radius: 12px;
							overflow: hidden;
							box-shadow: 0 2px 8px rgba(0,0,0,0.08);
						}
						
						.permissions-table thead {
							background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
							color: white;
						}
						
						.permissions-table thead th {
							font-weight: 600;
							padding: 16px 12px;
							border: none;
							font-size: 13px;
							text-transform: uppercase;
							letter-spacing: 0.5px;
						}
						
						.permissions-table tbody tr {
							border-bottom: 1px solid #f0f0f0;
							transition: all 0.2s ease;
						}
						
						.permissions-table tbody tr:hover {
							background-color: #f0fdf4;
						}
						
						.permissions-table tbody td {
							padding: 14px 12px;
							vertical-align: middle;
							font-size: 14px;
						}
						
						.btn-add-permission {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
							padding: 10px 20px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-add-permission:hover {
							transform: translateY(-2px);
							box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
							color: white;
						}
						
						.btn-reset {
							background: linear-gradient(135deg, #10b981 0%, #059669 100%);
							border: none;
							color: white;
							padding: 10px 20px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-reset:hover {
							transform: translateY(-2px);
							box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
							color: white;
						}
						
						.btn-delete-bulk {
							background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
							border: none;
							color: white;
							padding: 10px 20px;
							border-radius: 8px;
							font-weight: 500;
							font-size: 14px;
						}
						
						.btn-delete-bulk:hover {
							transform: translateY(-2px);
							box-shadow: 0 4px 12px rgba(220, 53, 69, 0.4);
							color: white;
						}
						
						.btn-edit-permission {
							background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
							border: none;
							color: white;
							padding: 6px 16px;
							border-radius: 6px;
							font-size: 13px;
							font-weight: 500;
						}
						
						.btn-edit-permission:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 8px rgba(102, 126, 234, 0.3);
							color: white;
						}
						
						.btn-delete-permission {
							background: linear-gradient(135deg, #dc3545 0%, #c82333 100%);
							border: none;
							color: white;
							padding: 6px 16px;
							border-radius: 6px;
							font-size: 13px;
							font-weight: 500;
							margin-left: 8px;
						}
						
						.btn-delete-permission:hover {
							transform: translateY(-1px);
							box-shadow: 0 4px 8px rgba(220, 53, 69, 0.3);
							color: white;
						}
					`}
				</style>
			
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
									<i className="fas fa-shield-alt me-3" style={{ color: '#1e7e34' }}></i>
									Permissions
								</h2>
								<p className="text-muted">Affichage des éléments 1 à {filteredPermissions.length} sur {permissions.length}</p>
							</div>
							<div className="d-flex gap-2">
								<button className="btn btn-outline-secondary" style={{ borderRadius: '8px' }}>
									Tableau de bord
								</button>
								<button className="btn btn-outline-secondary" style={{ borderRadius: '8px' }}>
									Permissions
								</button>
								<button className="btn btn-success" style={{ borderRadius: '8px' }}>
									Liste
								</button>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12 d-flex gap-2">
						<button 
							className="btn btn-add-permission"
							onClick={() => setView('add')}
						>
							<i className="fas fa-plus me-2"></i>Ajouter permission
						</button>
						<button 
							className="btn btn-reset"
							onClick={fetchPermissions}
						>
							<i className="fas fa-sync me-2"></i>Reset the Permissions
						</button>
						<button 
							className="btn btn-delete-bulk"
							onClick={handleBulkDelete}
							disabled={selectedItems.length === 0}
						>
							<i className="fas fa-trash me-2"></i>Delete
						</button>
					</div>
				</div>

				<div className="row mb-3">
					<div className="col-md-6">
						<span className="text-muted" style={{ fontSize: '14px' }}>25 enregistrement par page</span>
					</div>
					<div className="col-md-6">
						<div className="d-flex justify-content-end">
							<div className="input-group" style={{ maxWidth: '300px' }}>
								<span className="input-group-text bg-white border-end-0">
									Recherche:
								</span>
								<input 
									type="text" 
									className="form-control border-start-0" 
									placeholder="" 
									value={searchTerm}
									onChange={(e) => setSearchTerm(e.target.value)}
									style={{ borderRadius: '0 8px 8px 0' }}
								/>
							</div>
						</div>
					</div>
				</div>

				<div className="permissions-table">
					{loading ? (
						<div className="text-center py-5">
							<div className="spinner-border text-success"></div>
						</div>
					) : (
						<table className="w-100">
							<thead>
								<tr>
									<th style={{ width: '50px' }}>
										<input
											type="checkbox"
											checked={selectedItems.length === filteredPermissions.length && filteredPermissions.length > 0}
											onChange={toggleSelectAll}
											style={{ width: '18px', height: '18px', cursor: 'pointer' }}
										/>
									</th>
									<th>Nom</th>
									<th>Roles that have this permission</th>
									<th style={{ textAlign: 'right' }}>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredPermissions.length === 0 ? (
									<tr>
										<td colSpan="4" className="text-center py-4">
											<p className="text-muted mb-0">Aucune permission trouvée</p>
										</td>
									</tr>
								) : (
									filteredPermissions.map((permission) => (
										<tr key={permission.id}>
											<td>
												<input
													type="checkbox"
													checked={selectedItems.includes(permission.id)}
													onChange={() => toggleSelectItem(permission.id)}
													style={{ width: '18px', height: '18px', cursor: 'pointer' }}
												/>
											</td>
											<td style={{ fontWeight: '500', color: '#1f2937' }}>
												{permission.name}
											</td>
											<td>
												{permission.roles && permission.roles.length > 0 ? (
													permission.roles.map((role, index) => (
														<Badge key={index} bg="secondary" className="me-1">
															{role}
														</Badge>
													))
												) : (
													<span className="text-muted">-</span>
												)}
											</td>
											<td style={{ textAlign: 'right' }}>
												<button 
													className="btn btn-edit-permission"
													onClick={() => handleEditPermission(permission)}
												>
													<i className="fas fa-edit me-1"></i>Modifier
												</button>
												<button 
													className="btn btn-delete-permission"
													onClick={() => handleDeletePermission(permission.id, permission.name)}
												>
													<i className="fas fa-trash me-1"></i>Delete
												</button>
											</td>
										</tr>
									))
								)}
							</tbody>
						</table>
					)}
				</div>

				<div className="row mt-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<p className="text-muted mb-0">Affichage des éléments 1 à {filteredPermissions.length} sur {permissions.length}.</p>
							<div className="d-flex gap-2">
								<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '6px' }}>
									Précédent
								</button>
								<button className="btn btn-sm btn-success" style={{ borderRadius: '6px' }}>
									1
								</button>
								<button className="btn btn-sm btn-outline-secondary" style={{ borderRadius: '6px' }}>
									Suivant
								</button>
							</div>
						</div>
					</div>
				</div>
			</>
		);
	}

	// ADD VIEW
	if (view === 'add') {
		return (
			<>
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
									<i className="fas fa-shield-alt me-3" style={{ color: '#1e7e34' }}></i>
									Permissions
								</h2>
								<p className="text-muted">Ajouter Permission</p>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12">
						<button 
							className="btn btn-add-permission"
							onClick={() => setView('list')}
						>
							<i className="fas fa-arrow-left me-2"></i>Retour à la liste
						</button>
					</div>
				</div>

				<div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
					<div className="card-body p-4">
						<h4 className="mb-4">Ajouter une nouvelle permission</h4>
						
						<div className="mb-4">
							<label className="form-label fw-bold">Nom <span className="text-danger">*</span></label>
							<input
								type="text"
								className="form-control"
								value={formData.name}
								onChange={(e) => setFormData({ ...formData, name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: post-moderate"
							/>
							<small className="text-muted">Identifiant unique de la permission (sans espaces)</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Nom d'affichage</label>
							<input
								type="text"
								className="form-control"
								value={formData.display_name}
								onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: Modérer les posts"
							/>
							<small className="text-muted">Nom lisible de la permission</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Description</label>
							<textarea
								className="form-control"
								value={formData.description}
								onChange={(e) => setFormData({ ...formData, description: e.target.value })}
								style={{ borderRadius: '8px' }}
								rows="3"
								placeholder="Ex: Permission de modérer les annonces"
							/>
							<small className="text-muted">Description de la permission</small>
						</div>

						<div className="d-flex gap-2">
							<button 
								className="btn"
								onClick={handleAddPermission}
								disabled={!formData.name}
								style={{ 
									borderRadius: '8px',
									background: 'linear-gradient(135deg, #1e7e34 0%, #28a745 100%)',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-save me-2"></i>Enregistrer Et Retour À La List
							</button>
							<button 
								className="btn"
								onClick={() => setView('list')}
								style={{ 
									borderRadius: '8px',
									background: '#1f2937',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-times me-2"></i>Annuler
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}

	// EDIT VIEW
	if (view === 'edit') {
		return (
			<>
				<div className="row mb-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<div>
								<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
									<i className="fas fa-shield-alt me-3" style={{ color: '#1e7e34' }}></i>
									Permissions
								</h2>
								<p className="text-muted">Modifier Permission</p>
							</div>
						</div>
					</div>
				</div>

				<div className="row mb-4">
					<div className="col-12">
						<button 
							className="btn btn-add-permission"
							onClick={() => setView('list')}
						>
							<i className="fas fa-arrow-left me-2"></i>Retour à la liste
						</button>
					</div>
				</div>

				<div className="card" style={{ border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: '12px' }}>
					<div className="card-body p-4">
						<h4 className="mb-4">Modifier la permission</h4>
						
						<div className="mb-4">
							<label className="form-label fw-bold">Nom <span className="text-danger">*</span></label>
							<input
								type="text"
								className="form-control"
								value={formData.name}
								onChange={(e) => setFormData({ ...formData, name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: post-moderate"
							/>
							<small className="text-muted">Identifiant unique de la permission (sans espaces)</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Nom d'affichage</label>
							<input
								type="text"
								className="form-control"
								value={formData.display_name}
								onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
								style={{ borderRadius: '8px' }}
								placeholder="Ex: Modérer les posts"
							/>
							<small className="text-muted">Nom lisible de la permission</small>
						</div>

						<div className="mb-4">
							<label className="form-label fw-bold">Description</label>
							<textarea
								className="form-control"
								value={formData.description}
								onChange={(e) => setFormData({ ...formData, description: e.target.value })}
								style={{ borderRadius: '8px' }}
								rows="3"
								placeholder="Ex: Permission de modérer les annonces"
							/>
							<small className="text-muted">Description de la permission</small>
						</div>

						<div className="d-flex gap-2">
							<button 
								className="btn"
								onClick={handleUpdatePermission}
								disabled={!formData.name}
								style={{ 
									borderRadius: '8px',
									background: 'linear-gradient(135deg, #1e7e34 0%, #28a745 100%)',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-save me-2"></i>Enregistrer Et Retour À La List
							</button>
							<button 
								className="btn"
								onClick={() => setView('list')}
								style={{ 
									borderRadius: '8px',
									background: '#1f2937',
									border: 'none',
									color: 'white',
									padding: '12px 28px',
									fontWeight: '500',
									fontSize: '14px'
								}}
							>
								<i className="fas fa-times me-2"></i>Annuler
							</button>
						</div>
					</div>
				</div>
			</>
		);
	}

	return null;
};

export default Permissions;
